import * as React from 'react';
import { TicketLineDTO, TicketDTO, Res } from 'core/api';
import { VStack, HStack, Text } from '@chakra-ui/react';
import { TicketLines } from './TicketLines';
import * as UI from 'core/ui';

const formatMoney = UI.Format.asMoney;

type Props = {
	ticket: TicketDTO;
	selectedLine: Nullable<TicketLineDTO>;
	onSelect: (line: Nullable<TicketLineDTO>) => void;
	onEdit?: (line: TicketLineDTO) => void;
	onDelete?: (lineId: TicketLineDTO) => void;
	hasEditionInLine?: boolean;
	showTicketStatus?: boolean;
	expandedTicket?: boolean;
	numberOfLinesToShow?: number;
};

function buildServiceChargeLine(ticket: TicketDTO, ref?: React.RefObject<HTMLDivElement | null>) {
	if (ticket.ServiceChargeAmount === 0)
		return null;

	const lastLineId = ticket.Lines[ticket.Lines.length - 1].Id;

	return <HStack
		key={lastLineId + 1}
		data-line-id={lastLineId + 1}
		ref={ref}
		width={'full'}
		padding={'2'}
		border={'1px solid'}
		borderRadius={'md'}
		borderColor={'neutral.300'}
		gap={'2'}
		fontSize={'sm'}
		fontWeight={'normal'}
		color={{ base: '{colors.neutral.500}', _dark: '{colors.neutral.400}' }}
		justifyContent={'space-between'}
	>
		<HStack gap={'2'}>
			<Text display={'flex'} justifyContent={'flex-end'} width={'16'}></Text>
			<Text display={'flex'} justifyContent={'flex-end'}>
				{Res.Web.TICKET_LINES_CONTROL_SERVICE_CHARGE_LBL(ticket.AppliedServiceCharge.ChargePercentage)}
			</Text>
		</HStack>
		<Text display={'flex'} justifyContent={'flex-end'}>{formatMoney(ticket.ServiceChargeAmount)}</Text>
	</HStack>;
}

export const TicketControl = React.memo(function TicketControl(props: Props) {

	const { ticket, selectedLine, onSelect, onEdit, onDelete, hasEditionInLine, showTicketStatus, expandedTicket, numberOfLinesToShow } = props;

	const selectedLineRef = React.useRef<HTMLDivElement | null>(null);
	const serviceChargeLineRef = React.useRef<HTMLDivElement | null>(null);
	const serviceChargeLine = buildServiceChargeLine(ticket, serviceChargeLineRef);
	const someLineHasSeatNumber = ticket.Lines.some(line => !!line.SeatNumber);

	const isFirstScroll = React.useRef<boolean>(true);

	React.useEffect(() => {

		selectedLineRef.current?.scrollIntoView({
			// En el primer mount no queremos hacer scroll por un ticket que puede ser muy largo
			behavior: isFirstScroll.current ? 'instant' : 'smooth',
			block: 'nearest'
		});

		isFirstScroll.current = false;
	}, [selectedLine?.Id, selectedLine?.DiscountPercentage, selectedLine?.CashDiscount, selectedLine?.Addins.length, selectedLine?.Notes, selectedLine?.RemovedIngredients.length, expandedTicket]);

	React.useEffect(() => {
		if (ticket.ServiceChargeAmount > 0) {
			serviceChargeLineRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest'
			});
		}
	}, [ticket.ServiceChargeAmount]);

	return <VStack gap={'1'} w='full' h={'full'} overflowY={'auto'} paddingBottom={'1px'}>
		{!numberOfLinesToShow && numberOfLinesToShow !== 0 &&
			<HStack
				bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }}
				w='full'
				color={'white'}
				position={'sticky'}
				top={0}
				zIndex={1}
				borderBottomRadius={'md'}
			>
				<HStack
					w={'full'}
					p={2}
					borderRadius='md'
					justifyContent={'space-between'}
					bg={{ base: '{colors.neutral.700}', _dark: '{colors.blue.900}' }}
				>
					<HStack gap={'2'}>
						<Text display={'flex'} justifyContent={'flex-end'} width={'16'}>{Res.Web.TICKET_CONTROL_QTY()}</Text>
						<Text display={'flex'} justifyContent={'flex-end'}>{Res.Web.TICKET_CONTROL_PRODUCT()}</Text>
					</HStack>
					<HStack gap={'2'}>
						<Text display={'flex'} justifyContent={'flex-end'} width={'16'}>{Res.Web.TICKET_CONTROL_PRICE()}</Text>
						<Text display={'flex'} justifyContent={'flex-end'} width={'16'} marginRight={someLineHasSeatNumber ? '6' : '0'}>{Res.Web.TICKET_CONTROL_TOTAL()}</Text>
					</HStack>
				</HStack>
			</HStack>
		}
		{
			ticket.Lines.map((line) => {
				return <TicketLines key={line.Id}
					ref={line.Id === selectedLine?.Id ? selectedLineRef : undefined}
					line={line}
					selectedLine={selectedLine}
					onSelect={onSelect}
					onEdit={onEdit}
					onDelete={onDelete}
					hasEditionInLine={hasEditionInLine}
					showTicketStatus={showTicketStatus}
					expandedTicket={expandedTicket}
					someLineHasSeatNumber={someLineHasSeatNumber}
					numberOfLinesToShow={numberOfLinesToShow}
				/>;
			})
		}
		{serviceChargeLine}
	</VStack>;
}, (prev, next) =>
	// Comparo sólo las cosas que pueden cambiar entre renders. El resto de cosas sólo cambian
	// cuando se monta el componente por primera vez
	prev.ticket === next.ticket &&
	prev.selectedLine === next.selectedLine &&
	prev.expandedTicket === next.expandedTicket);